/**
 * Hierarchical Object Tree Node — Recursive collapsible component
 * Used in 4D Modelling for BIM model object hierarchy navigation
 */
import { ChevronDown, ChevronRight, FolderOpen, FolderClosed, File as FileIcon } from 'lucide-react';

export function ObjectTreeNode({ node, depth, expandedNodes, onToggle, selectedDbId, onSelect, highlightedDbId }) {
  const isExpanded = expandedNodes.has(node.dbId);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedDbId === node.dbId;
  const isHighlighted = highlightedDbId === node.dbId;

  return (
    <div>
      <button
        onClick={() => onSelect(node.dbId)}
        className={`w-full text-left text-xs py-1 px-1 flex items-center gap-1 rounded transition-colors
          ${isSelected ? 'bg-blue-500/20 text-blue-300' : isHighlighted ? 'bg-amber-500/15 text-amber-300' : 'hover:bg-accent'}`}
        style={{ paddingLeft: `${depth * 14 + 4}px` }}
        data-testid={`tree-node-${node.dbId}`}
        data-dbid={node.dbId}
      >
        {hasChildren ? (
          <span
            onClick={(e) => { e.stopPropagation(); onToggle(node.dbId); }}
            className="shrink-0 w-4 h-4 flex items-center justify-center cursor-pointer hover:bg-accent rounded"
          >
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </span>
        ) : (
          <span className="shrink-0 w-4 h-4" />
        )}
        {hasChildren ? (
          isExpanded ? <FolderOpen className="w-3 h-3 text-amber-500 shrink-0" /> : <FolderClosed className="w-3 h-3 text-amber-500/70 shrink-0" />
        ) : (
          <FileIcon className="w-3 h-3 text-slate-500 shrink-0" />
        )}
        <span className="truncate flex-1">{node.name || `Object ${node.dbId}`}</span>
        <span className="font-mono text-[9px] text-muted-foreground/50 shrink-0">#{node.dbId}</span>
      </button>
      {isExpanded && hasChildren && (
        <div>
          {node.children.map(child => (
            <ObjectTreeNode
              key={child.dbId}
              node={child}
              depth={depth + 1}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              selectedDbId={selectedDbId}
              onSelect={onSelect}
              highlightedDbId={highlightedDbId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Build hierarchical tree from flat object array
 */
export function buildObjectTree(flatObjects) {
  const map = {};
  const roots = [];
  for (const obj of flatObjects) {
    map[obj.dbId] = { ...obj, children: [] };
  }
  for (const obj of flatObjects) {
    const node = map[obj.dbId];
    if (obj.parentDbId != null && map[obj.parentDbId]) {
      map[obj.parentDbId].children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

/**
 * Find all ancestor dbIds for a given dbId (for auto-expand during navigation)
 */
export function findAncestors(flatObjects, dbId) {
  const parentMap = {};
  for (const obj of flatObjects) {
    parentMap[obj.dbId] = obj.parentDbId;
  }
  const ancestors = new Set();
  let current = parentMap[dbId];
  while (current != null) {
    ancestors.add(current);
    current = parentMap[current];
  }
  return ancestors;
}
